import { KNOWLEDGE_WORKFLOW } from '@bpinternal/const'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@bpinternal/ui-kit-next'
import { Check, FileText, Link, Upload } from 'lucide-react'
import { Ref, useEffect, useImperativeHandle, useRef } from 'react'
import { type Accept } from 'react-dropzone'
import { MinimalTiptapEditor, MinimalTiptapEditorHandle } from '~/components/Tiptap'
import { useStepNavStore } from '~/features/bots/stores/useStepNavStore'
import { getApiClient, useWorkflowStore } from '~/stores'
import { getUseBotOnboardingStore } from '../../../stores/useBotOnboardingStore'
import { getUseRichTextSourceStore } from '../../../stores/useRichTextSource'
import type { StepComponentHandle } from '../CreateBotOnboarding'
import { StepCardContent } from '../StepCard'
import { FilesTabContent } from './FilesTabContent'
import './styles.css'
import { UrlsTabContent } from './UrlsTabContent'

type KnowledgeStepProps = {
  botId: string
  workspaceId: string
}

export const KnowledgeStep = ({
  ref,
  botId,
  workspaceId,
}: KnowledgeStepProps & {
  ref: Ref<StepComponentHandle>
}) => {
  const useRichTextSourceStore = getUseRichTextSourceStore({ botId, workspaceId })
  const { content, setContent } = useRichTextSourceStore()
  const useBotOnboardingStore = getUseBotOnboardingStore({ botId, workspaceId })

  const uploadableFiles = useBotOnboardingStore((state) => state.knowledgeBase.uploadableFiles)
  const websiteUrlsTree = useBotOnboardingStore((state) => state.websiteUrlsTree)
  const setDeploymentWorkflowId = useBotOnboardingStore((state) => state.setDeploymentWorkflowId)
  const setTotalFileCount = useBotOnboardingStore((state) => state.setTotalFileCount)
  const register = useWorkflowStore((state) => state.register)

  const fileCount = uploadableFiles.filter((f) => f.status !== 'error').length
  const websiteCount = websiteUrlsTree.checked.length

  const setProgressBarStep = useStepNavStore((state) => state.setProgressBarStep)
  const editorRef = useRef<MinimalTiptapEditorHandle>(null)

  useEffect(() => {
    setProgressBarStep(4)
  }, [])

  useImperativeHandle(ref, () => ({
    handleNext: async () => {
      if (websiteCount === 0 && content.trim().length === 0) return

      const { workflow } = await getApiClient({ botId, workspaceId }).createWorkflow({
        name: KNOWLEDGE_WORKFLOW,
        status: 'pending',
        input: {
          urls: websiteUrlsTree.checked,
          richTextContent: content,
        },
      })

      register({ botId, workspaceId, workflow })
      setTotalFileCount(websiteCount + fileCount + (content.trim().length > 0 ? 1 : 0))
      setDeploymentWorkflowId(workflow.id)
    },
  }))

  const maxFiles = 10
  const maxSize = 1024 * 1024 * 2 // 2MB
  const accept: Accept = {
    'application/pdf': ['.pdf'],
    'text/html': ['.html'],
    'text/plain': ['.txt'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'text/markdown': ['.md'],
  }
  const disabled = false

  return (
    <StepCardContent
      title="Knowledge"
      description="Add knowledge sources to help your bot answer questions. You can add more later."
      className="flex h-full gap-6"
    >
      <Tabs
        defaultValue="urls"
        className="min-w-0 flex-1 space-y-4 rounded-xl sm:space-y-6 lg:border lg:border-neutral-100 lg:p-6"
      >
        <TabsList className="grid h-10 w-full grid-cols-3">
          <TabsTrigger value="urls" className="flex h-8 items-center gap-1 text-sm">
            <Link className="h-4 w-4 shrink-0" />
            <span>Websites</span>
            <span className="min-w-6 rounded-full bg-neutral-100">{websiteCount}</span>
          </TabsTrigger>
          <TabsTrigger value="files" className="flex h-8 items-center gap-1 text-sm">
            <Upload className="h-4 w-4" />
            <span>Files</span>
            <span className="min-w-6 rounded-full bg-neutral-100">{fileCount}</span>
          </TabsTrigger>
          <TabsTrigger value="text" className="flex h-8 items-center gap-1 text-sm">
            <FileText className="h-4 w-4" />
            <span>Text</span>
            {content.trim().length > 0 && <Check className="min-w-6 rounded-full bg-neutral-100" size={18} />}
          </TabsTrigger>
        </TabsList>

        <div className="max-h-[375px] overflow-auto">
          <TabsContent value="files" className="space-y-3 sm:space-y-4">
            <FilesTabContent
              botId={botId}
              workspaceId={workspaceId}
              accept={accept}
              maxSize={maxSize}
              maxFiles={maxFiles}
              disabled={disabled}
            />
          </TabsContent>
          <TabsContent value="urls" className="space-y-3 sm:space-y-4">
            <UrlsTabContent workspaceId={workspaceId} botId={botId} />
          </TabsContent>
          <TabsContent value="text" className="space-y-2">
            <div className="space-y-2">
              <MinimalTiptapEditor
                ref={editorRef}
                content={content}
                onChange={(richText) => setContent(String(richText))}
                className="h-80 w-full overflow-scroll text-sm"
                editorContentClassName="p-3 sm:p-5"
                output="html"
                placeholder="Add FAQs, documentation, or any text content your bot should reference"
                autofocus={true}
                editable={true}
                editorClassName="focus:outline-hidden"
              />
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </StepCardContent>
  )
}
KnowledgeStep.displayName = 'KnowledgeStep'
